/**
 * User Profile & Settings Page
 * 
 * Allows users to:
 * - View and edit profile information
 * - See profile completion percentage
 * - Set up MFA (Two-Factor Authentication)
 * - Manage notification preferences (Email, SMS, WhatsApp, Push)
 * - View trusted devices
 * - Delete account (GDPR)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  User, 
  Shield, 
  Bell, 
  Settings,
  Save,
  RefreshCw,
  Trash2,
  Download,
  AlertTriangle,
  CheckCircle2,
  Circle,
  Smartphone
} from 'lucide-react';
import MFASetup from '../components/MFASetup';
import usePushNotifications from '../hooks/usePushNotifications';
import api from '../lib/api';

const UserProfile = () => {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Profile form state
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
  });

  // Calculate profile completeness
  const calculateCompleteness = useCallback(() => {
    if (!user) return { percentage: 0, fields: [] };
    
    const fields = [
      { name: 'Email', complete: !!user.email, required: true },
      { name: 'First Name', complete: !!user.first_name },
      { name: 'Last Name', complete: !!user.last_name },
      { name: 'Phone Number', complete: !!user.phone },
      { name: 'Email Verified', complete: user.email_verified === true },
      { name: 'MFA Enabled', complete: user.mfa_enabled === true },
    ];
    
    const completedCount = fields.filter(f => f.complete).length;
    const percentage = Math.round((completedCount / fields.length) * 100);
    
    return { percentage, fields };
  }, [user]);

  const profileCompleteness = calculateCompleteness();

  // Load user profile
  useEffect(() => {
    if (user) {
      setProfile({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        email: user.email || '',
      });
    }
  }, [user]);

  // Save profile changes
  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      const response = await api.put('/auth/profile', {
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone,
      });
      
      if (response.data) {
        setUser({ ...user, ...response.data });
        toast.success('Profile updated successfully');
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // Export user data (GDPR)
  const handleExportData = async () => {
    try {
      setExporting(true);
      const response = await api.get('/auth/export-data', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `letwise-data-export-${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Data exported successfully');
    } catch (error) {
      console.error('Failed to export data:', error);
      toast.error('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Delete account (GDPR right to be forgotten)
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    try {
      setDeleting(true);
      await api.delete('/auth/account');
      toast.success('Account deleted successfully');
      logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to delete account:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl" data-testid="user-profile">
      <div className="mb-8">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Settings className="h-6 w-6" />
          Account Settings
        </h1>
        <p className="text-muted-foreground">
          Manage your profile, security settings, and preferences
        </p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <span className="hidden sm:inline">Profile</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">Security</span>
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            <span className="hidden sm:inline">Notifications</span>
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          {/* Profile Completeness Card */}
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Profile Completion</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-500 ${
                      profileCompleteness.percentage >= 80 ? 'bg-green-500' :
                      profileCompleteness.percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${profileCompleteness.percentage}%` }}
                  />
                </div>
                <span className="font-bold text-lg">{profileCompleteness.percentage}%</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {profileCompleteness.fields.map((field) => (
                  <div 
                    key={field.name}
                    className={`flex items-center gap-2 text-sm ${
                      field.complete ? 'text-green-600' : 'text-gray-400'
                    }`}
                  >
                    {field.complete ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      <Circle className="w-4 h-4" />
                    )}
                    {field.name}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>
                Update your personal information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-medium mb-2 block">First Name</label>
                  <Input
                    value={profile.first_name}
                    onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Last Name</label>
                  <Input
                    value={profile.last_name}
                    onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                    placeholder="Doe"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Email</label>
                <Input
                  value={profile.email}
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Contact support to change your email address
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Phone Number</label>
                <Input
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  placeholder="+44 7885 507742"
                />
              </div>
              
              <div className="pt-4">
                <Button onClick={handleSaveProfile} disabled={saving}>
                  {saving ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Data Management Card */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Data Management</CardTitle>
              <CardDescription>
                Export or delete your account data (GDPR)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div>
                  <h4 className="font-medium">Export Your Data</h4>
                  <p className="text-sm text-gray-600">Download a copy of all your personal data</p>
                </div>
                <Button 
                  variant="outline" 
                  onClick={handleExportData}
                  disabled={exporting}
                >
                  {exporting ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Export Data
                </Button>
              </div>

              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-red-700">Delete Account</h4>
                    <p className="text-sm text-red-600 mb-3">
                      This will permanently delete your account and all associated data. This action cannot be undone.
                    </p>
                    
                    {!showDeleteConfirm ? (
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => setShowDeleteConfirm(true)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete My Account
                      </Button>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm font-medium text-red-700">
                          Type DELETE to confirm:
                        </p>
                        <Input
                          value={deleteConfirmText}
                          onChange={(e) => setDeleteConfirmText(e.target.value)}
                          placeholder="Type DELETE"
                          className="max-w-xs border-red-300"
                        />
                        <div className="flex gap-2">
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={handleDeleteAccount}
                            disabled={deleting || deleteConfirmText !== 'DELETE'}
                          >
                            {deleting ? (
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4 mr-2" />
                            )}
                            Confirm Delete
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setShowDeleteConfirm(false);
                              setDeleteConfirmText('');
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <MFASetup />
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>
                Choose how you want to receive notifications
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Notification categories will be loaded dynamically */}
                <NotificationPreferences />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Notification Preferences Sub-component
const NotificationPreferences = () => {
  const [preferences, setPreferences] = useState({});
  const [loading, setLoading] = useState(true);
  const { isSupported, isEnabled, toggle, sendTest, loading: pushLoading } = usePushNotifications();

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await api.get('/notifications/preferences');
      setPreferences(response.data.preferences || {});
    } catch (error) {
      console.error('Failed to load preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (type, channel, enabled) => {
    try {
      const currentPref = preferences[type] || {};
      await api.put('/notifications/preferences', {
        notification_type: type,
        email_enabled: channel === 'email' ? enabled : currentPref.email_enabled,
        sms_enabled: channel === 'sms' ? enabled : currentPref.sms_enabled,
        whatsapp_enabled: channel === 'whatsapp' ? enabled : currentPref.whatsapp_enabled,
        push_enabled: channel === 'push' ? enabled : currentPref.push_enabled,
      });
      
      setPreferences({
        ...preferences,
        [type]: {
          ...currentPref,
          [`${channel}_enabled`]: enabled,
        },
      });
      
      toast.success('Preference updated');
    } catch (error) {
      console.error('Failed to update preference:', error);
      toast.error('Failed to update preference');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Group notification types
  const categories = {
    'Account': ['account_verification', 'password_reset', 'mfa_code', 'login_alert'],
    'Tenancy': ['rent_reminder', 'rent_overdue', 'lease_expiry', 'tenancy_update'],
    'Maintenance': ['repair_request', 'repair_update', 'inspection_scheduled'],
    'Listings': ['new_listing_match', 'viewing_confirmation', 'application_update'],
  };

  const formatTypeName = (type) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="space-y-6">
      {/* Push Notification Master Toggle */}
      {isSupported && (
        <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${isEnabled ? 'bg-emerald-500' : 'bg-gray-400'}`}>
                  <Smartphone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-medium">Push Notifications</h4>
                  <p className="text-sm text-gray-600">
                    {isEnabled ? 'Enabled - You will receive instant notifications' : 'Enable to receive real-time alerts'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isEnabled && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={sendTest}
                  >
                    Test
                  </Button>
                )}
                <Button 
                  variant={isEnabled ? "outline" : "default"}
                  size="sm"
                  onClick={toggle}
                  disabled={pushLoading}
                  className={isEnabled ? "" : "bg-emerald-600 hover:bg-emerald-700"}
                >
                  {pushLoading ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : isEnabled ? (
                    'Disable'
                  ) : (
                    'Enable Push'
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-category preferences */}
      {Object.entries(categories).map(([category, types]) => (
        <div key={category}>
          <h4 className="font-medium mb-3">{category}</h4>
          <div className="space-y-2">
            {types.map((type) => {
              const pref = preferences[type] || { email_enabled: true, push_enabled: true };
              return (
                <div 
                  key={type} 
                  className="flex items-center justify-between py-2 px-3 bg-muted/50 rounded-lg"
                >
                  <span className="text-sm">{formatTypeName(type)}</span>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={pref.email_enabled !== false}
                        onChange={(e) => updatePreference(type, 'email', e.target.checked)}
                        className="h-4 w-4 rounded"
                      />
                      Email
                    </label>
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={pref.sms_enabled === true}
                        onChange={(e) => updatePreference(type, 'sms', e.target.checked)}
                        className="h-4 w-4 rounded"
                      />
                      SMS
                    </label>
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={pref.whatsapp_enabled === true}
                        onChange={(e) => updatePreference(type, 'whatsapp', e.target.checked)}
                        className="h-4 w-4 rounded"
                      />
                      WhatsApp
                    </label>
                    {isSupported && (
                      <label className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={pref.push_enabled !== false}
                          onChange={(e) => updatePreference(type, 'push', e.target.checked)}
                          className="h-4 w-4 rounded"
                          disabled={!isEnabled}
                        />
                        Push
                      </label>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default UserProfile;
